import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useHistory } from "react-router-dom";
import {
  makeStyles,
  Paper,
  TextField,
  Button,
  IconButton,
  Checkbox,
  Chip,
  Typography,
  CircularProgress,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Input,
  InputAdornment,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import SearchIcon from "@material-ui/icons/Search";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import PersonIcon from "@material-ui/icons/Person";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import CloseIcon from "@material-ui/icons/Close";
import { toast } from "react-toastify";
import api from "../../services/api";
import ConfirmationModal from "../../components/ConfirmationModal";

const PRIORITY_LABELS = { high: "Alta", medium: "Média", low: "Baixa" };
const PRIORITY_COLORS = { high: "#e53935", medium: "#fb8c00", low: "#1e88e5" };
const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };
const DONE_PAGE_SIZE = 30;
const DAY_MS = 86400000;

const formatDate = date => {
  if (!date) return null;
  const d = new Date(date);
  return `${d.getUTCDate().toString().padStart(2, "0")}/${(d.getUTCMonth() + 1)
    .toString()
    .padStart(2, "0")}/${d.getUTCFullYear()}`;
};

// dueDate é salvo como data (meia-noite UTC); "hoje" precisa ser o dia local
// do usuário, senão à noite no Brasil (UTC-3) o prazo "vence" 3h antes.
const daysUntilDue = task => {
  if (!task.dueDate) return null;
  const due = new Date(task.dueDate);
  const now = new Date();
  const dueDay = Date.UTC(due.getUTCFullYear(), due.getUTCMonth(), due.getUTCDate());
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((dueDay - today) / DAY_MS);
};

const isOverdue = task => {
  if (task.completed) return false;
  const days = daysUntilDue(task);
  return days !== null && days < 0;
};

const GROUPS = [
  { key: "overdue", label: "Atrasadas", match: d => d !== null && d < 0 },
  { key: "today", label: "Hoje", match: d => d === 0 },
  { key: "week", label: "Próximos 7 dias", match: d => d !== null && d > 0 && d <= 7 },
  { key: "later", label: "Mais tarde", match: d => d !== null && d > 7 },
  { key: "nodate", label: "Sem prazo", match: d => d === null },
];

const SORTERS = {
  priority: (a, b) =>
    (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1) ||
    (daysUntilDue(a) ?? Infinity) - (daysUntilDue(b) ?? Infinity),
  dueDate: (a, b) =>
    (daysUntilDue(a) ?? Infinity) - (daysUntilDue(b) ?? Infinity) ||
    (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1),
  recent: (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
};

const TABS = [
  { key: "pending", label: "Pendentes" },
  { key: "overdue", label: "Atrasadas" },
  { key: "done", label: "Concluídas" },
];

const matchesSearch = (task, term) => {
  if (!term) return true;
  const t = term.toLowerCase();
  return (
    task.text?.toLowerCase().includes(t) ||
    task.contact?.name?.toLowerCase().includes(t) ||
    task.opportunity?.title?.toLowerCase().includes(t)
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 760,
    margin: "0 auto",
    [theme.breakpoints.down("xs")]: { padding: theme.spacing(2) },
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  summary: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  formRow: {
    display: "flex",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { flexDirection: "column" },
  },
  toolbar: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  toolbarRow: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(1),
  },
  search: {
    flex: 1,
    minWidth: 180,
    [theme.breakpoints.down("xs")]: { flexBasis: "100%" },
  },
  tabs: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
  },
  bulkBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    padding: theme.spacing(1, 1.5),
    marginTop: theme.spacing(1),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.action.selected,
  },
  bulkCount: { flex: 1, fontSize: "0.875rem", fontWeight: 600 },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    userSelect: "none",
    margin: theme.spacing(2, 0, 1),
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  groupHeaderOverdue: { color: "#e53935" },
  groupCount: {
    marginLeft: theme.spacing(1),
    fontWeight: 400,
  },
  taskCard: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[1],
    borderLeft: "4px solid transparent",
  },
  taskCardOverdue: {
    borderLeftColor: "#e53935",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(229,57,53,0.08)"
        : "rgba(229,57,53,0.04)",
  },
  taskCardCompleted: {
    opacity: 0.6,
  },
  taskCardSelected: {
    outline: `2px solid ${theme.palette.primary.main}`,
  },
  taskBody: {
    flex: 1,
    minWidth: 0,
    marginLeft: theme.spacing(1),
  },
  taskText: {
    fontSize: "0.95rem",
    wordBreak: "break-word",
  },
  taskTextCompleted: {
    textDecoration: "line-through",
    color: theme.palette.text.disabled,
  },
  taskMeta: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    flexWrap: "wrap",
  },
  priorityChip: {
    color: "#fff",
    height: 20,
    fontSize: "0.7rem",
  },
  linkChip: {
    height: 20,
    fontSize: "0.7rem",
    maxWidth: 220,
  },
  dueDateText: {
    fontSize: "0.78rem",
    color: theme.palette.text.secondary,
  },
  dueDateOverdue: {
    color: "#e53935",
    fontWeight: 600,
  },
  dueDateToday: {
    color: "#fb8c00",
    fontWeight: 600,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  checkboxRoot: {
    color: theme.palette.success.main,
    padding: 4,
    "&$checked": { color: theme.palette.success.main },
  },
  checked: {},
  inlineInput: {
    fontSize: "0.95rem",
    width: "100%",
  },
  loadMore: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
}));

const dueLabel = task => {
  const days = daysUntilDue(task);
  if (days === null) return null;
  if (task.completed) return `Prazo: ${formatDate(task.dueDate)}`;
  if (days < 0) return `⚠ Atrasada · ${formatDate(task.dueDate)}`;
  if (days === 0) return "Vence hoje";
  if (days === 1) return "Vence amanhã";
  return `Prazo: ${formatDate(task.dueDate)}`;
};

const ToDoList = () => {
  const classes = useStyles();
  const history = useHistory();

  const [pendingTasks, setPendingTasks] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  const [doneTasks, setDoneTasks] = useState([]);
  const [loadingDone, setLoadingDone] = useState(false);
  const [doneHasMore, setDoneHasMore] = useState(false);

  const [tab, setTab] = useState("pending");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("priority");
  const [collapsed, setCollapsed] = useState({});

  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");

  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkWorking, setBulkWorking] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [newText, setNewText] = useState("");
  const [newPriority, setNewPriority] = useState("medium");
  const [newDueDate, setNewDueDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await api.get("/tasks", { params: { status: "pending" } });
      setPendingTasks(data);
    } catch {
      toast.error("Erro ao carregar tarefas.");
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const fetchDone = useCallback(async (offset, searchParam) => {
    setLoadingDone(true);
    try {
      const { data } = await api.get("/tasks", {
        params: {
          status: "done",
          limit: DONE_PAGE_SIZE,
          offset,
          searchParam: searchParam || undefined,
        },
      });
      setDoneTasks(prev => (offset === 0 ? data : [...prev, ...data]));
      setDoneHasMore(data.length === DONE_PAGE_SIZE);
    } catch {
      toast.error("Erro ao carregar tarefas concluídas.");
    } finally {
      setLoadingDone(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
  }, [fetchPending]);

  // concluídas só são buscadas sob demanda (aba aberta), paginadas no servidor
  useEffect(() => {
    if (tab === "done") fetchDone(0, debouncedSearch);
  }, [tab, debouncedSearch, fetchDone]);

  useEffect(() => {
    setSelectedIds([]);
  }, [tab]);

  const pendingCount = pendingTasks.length;
  const overdueCount = useMemo(() => pendingTasks.filter(isOverdue).length, [pendingTasks]);

  const visiblePending = useMemo(
    () =>
      pendingTasks
        .filter(t => matchesSearch(t, debouncedSearch))
        .filter(t => (tab === "overdue" ? isOverdue(t) : true))
        .sort(SORTERS[sortBy]),
    [pendingTasks, debouncedSearch, tab, sortBy]
  );

  const groupedPending = useMemo(
    () =>
      GROUPS.map(g => ({
        ...g,
        tasks: visiblePending.filter(t => g.match(daysUntilDue(t))),
      })).filter(g => g.tasks.length > 0),
    [visiblePending]
  );

  const visibleTasks = tab === "done" ? doneTasks : visiblePending;

  const handleAdd = async () => {
    if (!newText.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/tasks", {
        text: newText.trim(),
        priority: newPriority,
        dueDate: newDueDate || null,
      });
      setPendingTasks(prev => [data, ...prev]);
      setNewText("");
      setNewPriority("medium");
      setNewDueDate("");
    } catch {
      toast.error("Erro ao criar tarefa.");
    } finally {
      setSaving(false);
    }
  };

  // PUT não devolve contato/oportunidade; preserva os que já vieram na listagem
  const applyUpdated = (original, data) => {
    const merged = { ...original, ...data };
    if (merged.completed) {
      setPendingTasks(prev => prev.filter(t => t.id !== merged.id));
      setDoneTasks(prev => [merged, ...prev.filter(t => t.id !== merged.id)]);
    } else {
      setDoneTasks(prev => prev.filter(t => t.id !== merged.id));
      setPendingTasks(prev =>
        prev.some(t => t.id === merged.id)
          ? prev.map(t => (t.id === merged.id ? merged : t))
          : [merged, ...prev]
      );
    }
  };

  const handleToggle = async task => {
    try {
      const { data } = await api.put(`/tasks/${task.id}`, {
        completed: !task.completed,
      });
      applyUpdated(task, data);
    } catch {
      toast.error("Erro ao atualizar tarefa.");
    }
  };

  const removeLocal = ids => {
    setPendingTasks(prev => prev.filter(t => !ids.includes(t.id)));
    setDoneTasks(prev => prev.filter(t => !ids.includes(t.id)));
  };

  const handleDelete = async id => {
    try {
      await api.delete(`/tasks/${id}`);
      removeLocal([id]);
    } catch {
      toast.error("Erro ao excluir tarefa.");
    }
  };

  const handleEditStart = task => {
    setEditingId(task.id);
    setEditingText(task.text);
  };

  const handleEditSave = async task => {
    if (!editingText.trim() || editingText.trim() === task.text) {
      setEditingId(null);
      return;
    }
    try {
      const { data } = await api.put(`/tasks/${task.id}`, {
        text: editingText.trim(),
      });
      applyUpdated(task, data);
    } catch {
      toast.error("Erro ao salvar tarefa.");
    } finally {
      setEditingId(null);
    }
  };

  const toggleSelected = id =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds([]);
  };

  const allVisibleSelected =
    visibleTasks.length > 0 && visibleTasks.every(t => selectedIds.includes(t.id));

  const handleSelectAll = () =>
    setSelectedIds(allVisibleSelected ? [] : visibleTasks.map(t => t.id));

  const handleBulkToggle = async () => {
    const targets = visibleTasks.filter(t => selectedIds.includes(t.id));
    const completed = tab !== "done";
    setBulkWorking(true);
    const results = await Promise.allSettled(
      targets.map(t => api.put(`/tasks/${t.id}`, { completed }))
    );
    results.forEach((r, i) => {
      if (r.status === "fulfilled") applyUpdated(targets[i], r.value.data);
    });
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed) toast.error(`${failed} tarefa(s) não puderam ser atualizadas.`);
    setBulkWorking(false);
    exitSelectMode();
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    setBulkWorking(true);
    const results = await Promise.allSettled(ids.map(id => api.delete(`/tasks/${id}`)));
    removeLocal(ids.filter((_, i) => results[i].status === "fulfilled"));
    const failed = results.filter(r => r.status === "rejected").length;
    if (failed) toast.error(`${failed} tarefa(s) não puderam ser excluídas.`);
    setBulkWorking(false);
    exitSelectMode();
  };

  const handleConfirmDelete = () => {
    if (confirmDelete === "bulk") handleBulkDelete();
    else if (confirmDelete) handleDelete(confirmDelete.id);
  };

  const renderTask = task => {
    const overdue = isOverdue(task);
    const days = daysUntilDue(task);
    const selected = selectedIds.includes(task.id);
    const label = dueLabel(task);

    return (
      <Paper
        key={task.id}
        className={[
          classes.taskCard,
          overdue && classes.taskCardOverdue,
          task.completed && !selectMode && classes.taskCardCompleted,
          selected && classes.taskCardSelected,
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={selectMode ? () => toggleSelected(task.id) : undefined}
        style={selectMode ? { cursor: "pointer" } : undefined}
      >
        {selectMode ? (
          <Checkbox
            edge="start"
            size="small"
            color="primary"
            checked={selected}
            onClick={e => e.stopPropagation()}
            onChange={() => toggleSelected(task.id)}
            style={{ padding: 4 }}
          />
        ) : (
          <Checkbox
            edge="start"
            checked={task.completed}
            onChange={() => handleToggle(task)}
            classes={{ root: classes.checkboxRoot, checked: classes.checked }}
            size="small"
          />
        )}

        <div className={classes.taskBody}>
          {editingId === task.id ? (
            <Input
              className={classes.inlineInput}
              value={editingText}
              onChange={e => setEditingText(e.target.value)}
              onBlur={() => handleEditSave(task)}
              onKeyDown={e => {
                if (e.key === "Enter") handleEditSave(task);
                if (e.key === "Escape") setEditingId(null);
              }}
              autoFocus
              fullWidth
            />
          ) : (
            <div className={`${classes.taskText} ${task.completed ? classes.taskTextCompleted : ""}`}>
              {task.text}
            </div>
          )}

          <div className={classes.taskMeta}>
            <Chip
              label={PRIORITY_LABELS[task.priority] || task.priority}
              size="small"
              className={classes.priorityChip}
              style={{ backgroundColor: PRIORITY_COLORS[task.priority] }}
            />
            {label && (
              <span
                className={[
                  classes.dueDateText,
                  overdue && classes.dueDateOverdue,
                  !task.completed && days === 0 && classes.dueDateToday,
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {label}
              </span>
            )}
            {task.contact && (
              <Tooltip title="Abrir contato">
                <Chip
                  icon={<PersonIcon />}
                  label={task.contact.name}
                  size="small"
                  variant="outlined"
                  className={classes.linkChip}
                  clickable={!selectMode}
                  onClick={
                    selectMode ? undefined : () => history.push(`/contacts/${task.contact.id}/profile`)
                  }
                />
              </Tooltip>
            )}
            {task.opportunity && (
              <Tooltip title="Abrir CRM">
                <Chip
                  icon={<TrendingUpIcon />}
                  label={task.opportunity.title}
                  size="small"
                  variant="outlined"
                  className={classes.linkChip}
                  clickable={!selectMode}
                  onClick={selectMode ? undefined : () => history.push("/crm")}
                />
              </Tooltip>
            )}
          </div>
        </div>

        {!selectMode && (
          <div className={classes.actions}>
            {!task.completed && editingId !== task.id && (
              <Tooltip title="Editar">
                <IconButton size="small" onClick={() => handleEditStart(task)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Excluir">
              <IconButton size="small" onClick={() => setConfirmDelete(task)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </Paper>
    );
  };

  const renderEmpty = () => {
    let msg = "Nenhuma tarefa encontrada.";
    if (debouncedSearch) msg = `Nenhuma tarefa encontrada para "${debouncedSearch}".`;
    else if (tab === "pending") msg = "Tudo em dia! Nenhuma tarefa pendente.";
    else if (tab === "overdue") msg = "Nenhuma tarefa atrasada.";
    else if (tab === "done") msg = "Nenhuma tarefa concluída ainda.";
    return (
      <div className={classes.emptyState}>
        <Typography>{msg}</Typography>
      </div>
    );
  };

  const renderList = () => {
    if (tab === "done") {
      if (loadingDone && doneTasks.length === 0) {
        return (
          <div style={{ textAlign: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        );
      }
      if (doneTasks.length === 0) return renderEmpty();
      return (
        <>
          {doneTasks.map(renderTask)}
          {doneHasMore && (
            <div className={classes.loadMore}>
              <Button
                variant="outlined"
                size="small"
                disabled={loadingDone}
                onClick={() => fetchDone(doneTasks.length, debouncedSearch)}
                startIcon={loadingDone ? <CircularProgress size={14} /> : null}
              >
                Carregar mais
              </Button>
            </div>
          )}
        </>
      );
    }

    if (loadingPending) {
      return (
        <div style={{ textAlign: "center", padding: 32 }}>
          <CircularProgress />
        </div>
      );
    }
    if (visiblePending.length === 0) return renderEmpty();
    if (tab === "overdue") return visiblePending.map(renderTask);

    return groupedPending.map(group => {
      const isCollapsed = !!collapsed[group.key];
      return (
        <div key={group.key}>
          <div
            className={`${classes.groupHeader} ${group.key === "overdue" ? classes.groupHeaderOverdue : ""}`}
            onClick={() => setCollapsed(prev => ({ ...prev, [group.key]: !prev[group.key] }))}
          >
            {isCollapsed ? <ChevronRightIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            {group.label}
            <span className={classes.groupCount}>({group.tasks.length})</span>
          </div>
          {!isCollapsed && group.tasks.map(renderTask)}
        </div>
      );
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h5" className={classes.title}>
          Minhas Tarefas
        </Typography>
        <Typography className={classes.summary}>
          {pendingCount} pendente{pendingCount !== 1 ? "s" : ""}
          {overdueCount > 0 && (
            <span style={{ color: "#e53935", marginLeft: 8 }}>
              · {overdueCount} atrasada{overdueCount !== 1 ? "s" : ""}
            </span>
          )}
        </Typography>
      </div>

      <Paper className={classes.form}>
        <TextField
          label="Nova tarefa"
          variant="outlined"
          size="small"
          fullWidth
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyPress={e => e.key === "Enter" && handleAdd()}
        />
        <div className={classes.formRow}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
            <InputLabel>Prioridade</InputLabel>
            <Select
              value={newPriority}
              onChange={e => setNewPriority(e.target.value)}
              label="Prioridade"
            >
              <MenuItem value="high">Alta</MenuItem>
              <MenuItem value="medium">Média</MenuItem>
              <MenuItem value="low">Baixa</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Prazo"
            type="date"
            variant="outlined"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={newDueDate}
            onChange={e => setNewDueDate(e.target.value)}
            style={{ flex: 1 }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleAdd}
            disabled={saving || !newText.trim()}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
            style={{ whiteSpace: "nowrap" }}
          >
            Adicionar
          </Button>
        </div>
      </Paper>

      <div className={classes.toolbar}>
        <div className={classes.toolbarRow}>
          <TextField
            className={classes.search}
            placeholder="Buscar tarefa, contato ou oportunidade"
            variant="outlined"
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {tab !== "done" && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 150 }}>
              <InputLabel>Ordenar por</InputLabel>
              <Select value={sortBy} onChange={e => setSortBy(e.target.value)} label="Ordenar por">
                <MenuItem value="priority">Prioridade</MenuItem>
                <MenuItem value="dueDate">Prazo</MenuItem>
                <MenuItem value="recent">Mais recentes</MenuItem>
              </Select>
            </FormControl>
          )}
        </div>

        <div className={classes.tabs}>
          {TABS.map(t => {
            let label = t.label;
            if (t.key === "pending") label = `${t.label} (${pendingCount})`;
            if (t.key === "overdue" && overdueCount > 0) label = `${t.label} (${overdueCount})`;
            return (
              <Chip
                key={t.key}
                label={label}
                onClick={() => setTab(t.key)}
                color={tab === t.key ? "primary" : "default"}
                variant={tab === t.key ? "default" : "outlined"}
                size="small"
              />
            );
          })}
          <div style={{ flex: 1 }} />
          {!selectMode && visibleTasks.length > 0 && (
            <Button size="small" onClick={() => setSelectMode(true)}>
              Selecionar
            </Button>
          )}
        </div>

        {selectMode && (
          <div className={classes.bulkBar}>
            <Checkbox
              size="small"
              color="primary"
              checked={allVisibleSelected}
              indeterminate={selectedIds.length > 0 && !allVisibleSelected}
              onChange={handleSelectAll}
              style={{ padding: 4 }}
            />
            <span className={classes.bulkCount}>
              {selectedIds.length} selecionada{selectedIds.length !== 1 ? "s" : ""}
            </span>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={!selectedIds.length || bulkWorking}
              onClick={handleBulkToggle}
            >
              {tab === "done" ? "Reabrir" : "Concluir"}
            </Button>
            <Button
              size="small"
              variant="outlined"
              disabled={!selectedIds.length || bulkWorking}
              onClick={() => setConfirmDelete("bulk")}
            >
              Excluir
            </Button>
            <Button size="small" onClick={exitSelectMode} disabled={bulkWorking}>
              Cancelar
            </Button>
          </div>
        )}
      </div>

      {renderList()}

      <ConfirmationModal
        title={confirmDelete === "bulk" ? "Excluir tarefas" : "Excluir tarefa"}
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleConfirmDelete}
      >
        {confirmDelete === "bulk"
          ? `Excluir ${selectedIds.length} tarefa(s) selecionada(s)? Essa ação não pode ser desfeita.`
          : "Excluir esta tarefa? Essa ação não pode ser desfeita."}
      </ConfirmationModal>
    </div>
  );
};

export default ToDoList;
